import { useEffect } from 'react';
import { useAnalyticsContext } from '../contexts/AnalyticsContext';
import { trackPerformance } from '../utils/analytics';

export const usePerformanceTracking = () => {
  const analytics = useAnalyticsContext();

  useEffect(() => {
    // Track Web Vitals
    if ('web-vital' in window) {
      import('web-vitals')
        .then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
          getCLS(metric => {
            trackPerformance('CLS', metric.value);
            analytics.trackEvent('web_vitals', {
              metric_name: 'CLS',
              metric_value: metric.value,
              metric_rating: metric.rating,
            });
          });

          getFID(metric => {
            trackPerformance('FID', metric.value);
            analytics.trackEvent('web_vitals', {
              metric_name: 'FID',
              metric_value: metric.value,
              metric_rating: metric.rating,
            });
          });

          getFCP(metric => {
            trackPerformance('FCP', metric.value);
            analytics.trackEvent('web_vitals', {
              metric_name: 'FCP',
              metric_value: metric.value,
              metric_rating: metric.rating,
            });
          });

          getLCP(metric => {
            trackPerformance('LCP', metric.value);
            analytics.trackEvent('web_vitals', {
              metric_name: 'LCP',
              metric_value: metric.value,
              metric_rating: metric.rating,
            });
          });

          getTTFB(metric => {
            trackPerformance('TTFB', metric.value);
            analytics.trackEvent('web_vitals', {
              metric_name: 'TTFB',
              metric_value: metric.value,
              metric_rating: metric.rating,
            });
          });
        })
        .catch(() => {
          // web-vitals library not available
        });
    }

    // Track page load time
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        const pageLoadTime = navigation.loadEventEnd - navigation.fetchStart;
        trackPerformance('page_load_time', pageLoadTime);

        analytics.trackEvent('page_performance', {
          load_time: pageLoadTime,
          dom_content_loaded:
            navigation.domContentLoadedEventEnd - navigation.fetchStart,
          first_paint: navigation.responseEnd - navigation.fetchStart,
        });
      }
    });

    // Track resource loading times
    const observer = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        if (entry.entryType === 'resource') {
          const resource = entry as PerformanceResourceTiming;

          // Track slow resources (>2 seconds)
          if (resource.duration > 2000) {
            analytics.trackEvent('slow_resource', {
              resource_name: resource.name,
              duration: resource.duration,
              type: resource.initiatorType,
            });
          }
        }
      });
    });

    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch (e) {
      // Performance Observer not supported
    }

    return () => {
      observer.disconnect();
    };
  }, [analytics]);

  // Manual performance tracking functions
  const trackCustomMetric = (
    name: string,
    value: number,
    unit: string = 'ms'
  ) => {
    trackPerformance(name, value);
    analytics.trackEvent('custom_performance', {
      metric_name: name,
      metric_value: value,
      unit,
    });
  };

  const trackUserInteraction = (action: string, duration: number) => {
    analytics.trackEvent('user_interaction_timing', {
      action,
      duration,
    });
  };

  return {
    trackCustomMetric,
    trackUserInteraction,
  };
};
